import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "react-query";

import { PageLayout, ActionsRow } from "components/PageLayout";
import { BoldText, MediumText, RegularText } from "components/Text";
import { Button } from "components/Button";
import { Input } from "components/Input";
import { Radio } from "components/Radio";
import { SimpleList } from "components/SimpleList";
import { VerticalSpace, HorizontalSpace } from "components/Spacing";
import { useModal, ConfirmationModal, InfoModal } from "components/Modal";

import {
  apiGetList,
  apiPost,
  apiPut,
  apiDelete,
  getErrorMessage,
} from "services/api";

import styles from "./styles.module.scss";

const TEMPLATES_KEY = ["cheque-print-templates"];

const getTemplates = () =>
  apiGetList("print-templates", { page: 1, pageSize: 1000 });

const KIND_OPTIONS = [
  { value: "slip", label: "Слип" },
  { value: "nakladnaya", label: "Накладная" },
  { value: "other", label: "Прочее" },
];

const getKind = (template) => {
  if (!template) return "other";
  if (Number(template.slipdefault) === 1) return "slip";
  if (Number(template.nakladnayadefault) === 1) return "nakladnaya";
  return "other";
};

const TemplateEditor = ({ template, isNew, onCreated, onDeleted }) => {
  const queryClient = useQueryClient();
  const { openModal, closeModal } = useModal();

  const [name, setName] = useState("");
  const [kind, setKind] = useState("other");
  const [value, setValue] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setError("");
    setName(isNew ? "" : template?.name ?? "");
    setKind(isNew ? "other" : getKind(template));
    setValue(isNew ? "" : template?.value ?? "");
  }, [template, isNew]);

  const handleSave = async () => {
    if (!name.trim()) {
      openModal(<InfoModal title="Укажите название" onClose={closeModal} />);
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = {
        name: name.trim(),
        value: value || null,
        slipdefault: kind === "slip" ? 1 : 0,
        nakladnayadefault: kind === "nakladnaya" ? 1 : 0,
        otherdefault: kind === "other" ? 1 : 0,
      };
      if (isNew) {
        const created = await apiPost("print-templates", payload);
        queryClient.invalidateQueries(TEMPLATES_KEY);
        onCreated?.(created?.printtemplateid);
      } else {
        await apiPut("print-templates", {
          ...payload,
          printtemplateid: template.printtemplateid,
        });
        queryClient.invalidateQueries(TEMPLATES_KEY);
      }
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    if (isNew || !template) return;
    openModal(
      <ConfirmationModal
        title="Удалить этот шаблон?"
        onConfirm={async () => {
          try {
            await apiDelete(`print-templates/${template.printtemplateid}`);
            queryClient.invalidateQueries(TEMPLATES_KEY);
            onDeleted?.();
            closeModal();
          } catch (e) {
            openModal(
              <InfoModal title={getErrorMessage(e)} onClose={closeModal} />
            );
          }
        }}
        onClose={closeModal}
      />
    );
  };

  return (
    <PageLayout
      className={styles.rightPanel}
      header={
        <BoldText>
          {isNew ? "Новый шаблон" : name || template?.name || "Шаблон"}
        </BoldText>
      }
      footer={
        <ActionsRow className={styles.editorFooter}>
          <div>
            {!isNew && (
              <Button
                icon="delete"
                onClick={handleDelete}
                disabled={saving}
                title="Удалить шаблон"
              />
            )}
          </div>
          <div className={styles.footerRight}>
            {!!error && (
              <RegularText size={13} color="danger">
                {error}
              </RegularText>
            )}
            <Button
              icon="galochka"
              variant="secondary"
              onClick={handleSave}
              loading={saving}
            >
              Сохранить
            </Button>
          </div>
        </ActionsRow>
      }
    >
      <Input
        label="Название"
        labelWidth={102}
        value={name}
        onChange={(ev) => setName(ev.target.value)}
        placeholder="Название шаблона"
        inputWidth={320}
      />
      <VerticalSpace size={16} />
      <div className={styles.kindRow}>
        <MediumText>Тип шаблона:</MediumText>
        <HorizontalSpace size={16} />
        {KIND_OPTIONS.map((option) => (
          <React.Fragment key={option.value}>
            <Radio
              id={`kind-${option.value}`}
              name="template-kind"
              label={option.label}
              checked={kind === option.value}
              onChange={() => setKind(option.value)}
            />
            <HorizontalSpace size={16} />
          </React.Fragment>
        ))}
      </div>
      <VerticalSpace size={16} />
      <textarea
        className={styles.valueTextarea}
        rows={18}
        value={value}
        onChange={(ev) => setValue(ev.target.value)}
        spellCheck={false}
        placeholder="Текст шаблона (сохраняются пробелы и переносы строк)"
      />
    </PageLayout>
  );
};

export const ChequePrintTemplatesPage = () => {
  const templatesQuery = useQuery(TEMPLATES_KEY, getTemplates);
  const templates = templatesQuery.data?.items ?? [];

  // selected: null | "new" | printtemplateid
  const [selected, setSelected] = useState(null);

  // Автовыбор после загрузки/изменения списка
  useEffect(() => {
    if (templatesQuery.isLoading) return;
    if (!templates.length) {
      setSelected("new");
      return;
    }
    if (selected === null) {
      setSelected(templates[0].printtemplateid);
      return;
    }
    if (selected === "new") return;
    if (!templates.some((t) => t.printtemplateid === selected)) {
      setSelected(templates[0].printtemplateid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates, templatesQuery.isLoading]);

  const activeTemplate =
    selected !== "new"
      ? templates.find((t) => t.printtemplateid === selected)
      : null;

  return (
    <div className={styles.page}>
      <PageLayout
        className={styles.leftPanel}
        header={<BoldText>Шаблоны чеков</BoldText>}
        footer={
          <ActionsRow>
            <Button
              icon="plus"
              onClick={() => setSelected("new")}
              title="Добавить шаблон"
            />
          </ActionsRow>
        }
      >
        <SimpleList
          items={templates}
          rowKey="printtemplateid"
          activeId={selected === "new" ? null : selected}
          loading={templatesQuery.isLoading}
          error={
            templatesQuery.isError
              ? getErrorMessage(templatesQuery.error)
              : ""
          }
          onRetry={templatesQuery.refetch}
          onRowClick={(t) => setSelected(t.printtemplateid)}
        />
      </PageLayout>
      <TemplateEditor
        template={activeTemplate}
        isNew={selected === "new"}
        onCreated={(id) => id && setSelected(id)}
        onDeleted={() => setSelected(null)}
      />
    </div>
  );
};
